import React, { useState, useMemo } from 'react';
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import useBookings from '../hooks/useBookings';
import useClients from '../hooks/useClients';
import { getSlotDates } from '../../data/constants';

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 0 }),
  getDay,
  locales: { 'en-US': enUS },
});

const TIER_COLORS = {
  'The Transformation': '#C9A84C',
  'The Alchemy': '#68caf1',
  'The Foundation': '#10b981',
  maintenance: '#8b5cf6',
  cancelled: '#ef4444',
};

function getColor(event) {
  if (event.eventType === 'maintenance') return '#8b5cf6';
  if (event.resource?.status === 'cancelled') return '#ef4444';
  return TIER_COLORS[event.resource?.tier || event.resource?.service_type] || '#9ca3af';
}

export default function CalendarPage() {
  const { bookings, loading } = useBookings();
  const { clients, loading: clientsLoading } = useClients();
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState('month');
  const [date, setDate] = useState(new Date());

  // Real weekend/job booking events
  const bookingEvents = useMemo(() => bookings
    .filter(b => b.scheduled_at)
    .map(b => {
      const start = b.scheduled_at.toDate ? b.scheduled_at.toDate() : new Date(b.scheduled_at);
      const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
      return {
        id: b.id,
        title: `${b.client_name || 'Client'} — ${b.tier || b.service_type || 'Service'}`,
        start,
        end,
        eventType: 'booking',
        resource: b,
      };
    }), [bookings]);

  // Predictive maintenance events — next 6 months per active member
  const maintenanceEvents = useMemo(() => {
    const activeMembers = clients.filter(c => c.type === 'maintenance' && c.status === 'active' && c.slot);
    const events = [];
    activeMembers.forEach(client => {
      const dates = getSlotDates(client.slot, 6);
      dates.forEach(date => {
        events.push({
          id: `maint-${client.id}-${date.toISOString()}`,
          title: `${client.name} — Maintenance`,
          start: date,
          end: new Date(date.getTime() + 2 * 60 * 60 * 1000),
          eventType: 'maintenance',
          resource: { clientId: client.id, clientName: client.name, slot: client.slot },
        });
      });
    });
    return events;
  }, [clients]);

  const events = useMemo(() => [...bookingEvents, ...maintenanceEvents], [bookingEvents, maintenanceEvents]);

  const eventStyleGetter = (event) => ({
    style: {
      backgroundColor: getColor(event),
      border: 'none',
      borderRadius: '6px',
      color: event.resource?.status === 'cancelled' ? '#fff' : '#000',
      fontSize: '12px',
      padding: '2px 6px',
    },
  });

  return (
    <div className="p-6 pb-24 md:pb-6 h-full">
      <h2 className="text-xl font-bold text-gray-900 mb-4">Calendar</h2>

      {/* Legend */}
      <div className="flex gap-4 mb-4 flex-wrap">
        {[
          { label: 'Transformation', color: '#C9A84C' },
          { label: 'Alchemy', color: '#68caf1' },
          { label: 'Foundation', color: '#10b981' },
          { label: 'Maintenance', color: '#8b5cf6' },
          { label: 'Cancelled', color: '#ef4444' },
        ].map(({ label, color }) => (
          <span key={label} className="flex items-center gap-1.5 text-xs text-gray-600">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
            {label}
          </span>
        ))}
      </div>

      {loading || clientsLoading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C9A84C]" />
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm p-4" style={{ height: 600 }}>
          <BigCalendar
            localizer={localizer}
            events={events}
            view={view}
            onView={setView}
            date={date}
            onNavigate={setDate}
            eventPropGetter={eventStyleGetter}
            onSelectEvent={e => setSelected({ ...e.resource, eventType: e.eventType, start: e.start, id: e.id })}
            style={{ height: '100%' }}
          />
        </div>
      )}

      {/* Event detail drawer */}
      {selected && (
        <div className="fixed inset-y-0 right-0 w-80 bg-white shadow-2xl z-40 flex flex-col">
          <div className="flex items-center justify-between p-5 border-b border-gray-100">
            <h3 className="font-bold text-gray-900">
              {selected.eventType === 'maintenance' ? 'Maintenance Visit' : 'Job Detail'}
            </h3>
            <button onClick={() => setSelected(null)}><X size={20} className="text-gray-400" /></button>
          </div>
          <div className="p-5 space-y-3 flex-1 overflow-y-auto">
            <p className="font-semibold text-gray-800">{selected.client_name || selected.clientName}</p>
            {selected.eventType === 'maintenance' ? (
              <>
                <span className="inline-block text-xs px-2 py-1 rounded-full font-medium bg-purple-100 text-purple-800">
                  Maintenance Program
                </span>
                {selected.start && (
                  <p className="text-sm text-gray-500">
                    {format(selected.start, 'EEEE, MMMM d, yyyy — h:mm a')}
                  </p>
                )}
                <p className="text-xs text-gray-400">Predictive schedule based on assigned slot</p>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600">{selected.tier || selected.service_type}</p>
                {selected.scheduled_at && (
                  <p className="text-sm text-gray-500">
                    {format(
                      selected.scheduled_at.toDate ? selected.scheduled_at.toDate() : new Date(selected.scheduled_at),
                      'EEEE, MMMM d, yyyy — h:mm a'
                    )}
                  </p>
                )}
                <p className="text-sm font-medium">
                  Price: <span className="text-gray-900">${selected.price || 0}</span>
                </p>
                <span className={`inline-block text-xs px-2 py-1 rounded-full font-medium ${
                  selected.status === 'completed' ? 'bg-emerald-100 text-emerald-800'
                  : selected.status === 'cancelled' ? 'bg-red-100 text-red-700'
                  : 'bg-blue-100 text-blue-800'
                }`}>
                  {selected.status}
                </span>
              </>
            )}
          </div>
          <div className="p-5 border-t border-gray-100">
            {selected.eventType === 'maintenance' ? (
              <Link
                to={`/admin/clients/${selected.clientId}`}
                className="block w-full text-center bg-[#8b5cf6] text-white rounded-lg py-2 font-semibold text-sm hover:bg-[#7c3aed]"
              >
                View Client Profile
              </Link>
            ) : (
              <div className="space-y-2">
                <Link
                  to={`/admin/jobs/${selected.id}`}
                  className="block w-full text-center bg-[#C9A84C] text-black rounded-lg py-2 font-semibold text-sm hover:bg-[#b8943d]"
                >
                  Open Full Job
                </Link>
                {selected.client_id && (
                  <Link
                    to={`/admin/clients/${selected.client_id}`}
                    className="block w-full text-center border border-gray-200 text-gray-700 rounded-lg py-2 font-semibold text-sm hover:bg-gray-50"
                  >
                    View Client Profile
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {selected && (
        <div className="fixed inset-0 bg-black/30 z-30" onClick={() => setSelected(null)} />
      )}
    </div>
  );
}
